import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";

// Photo cache - persists during session
const photoCache = new Map();

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

async function fetchPhoto(url) {
  if (photoCache.has(url)) return photoCache.get(url);

  const headers = await getAuthHeaders();
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error("Failed to load photo");

  const blob = await res.blob();

  // Use FileReader instead of createObjectURL for better iOS compatibility
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      photoCache.set(url, dataUrl);
      resolve(dataUrl);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Thumbnail version for grids
export function AuthThumb({ filename, alt, onClick }) {
  const [src, setSrc] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || !filename) return;
    let cancelled = false;

    async function load() {
      try {
        const dataUrl = await fetchPhoto(`/api/thumbs/${filename}`);
        if (!cancelled) setSrc(dataUrl);
      } catch (err) {
        try {
          const dataUrl = await fetchPhoto(`/api/photos/${filename}`);
          if (!cancelled) setSrc(dataUrl);
        } catch (e) {
          console.error("Photo load error:", e);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [isVisible, filename]);

  return (
    <div ref={containerRef} className="auth-photo-container" onClick={onClick}>
      {src
        ? <img src={src} alt={alt} className="auth-photo-img" />
        : <div className="photo-placeholder" />
      }
    </div>
  );
}

// Full resolution version for lightbox
export function AuthPhoto({ filename, alt, className }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!filename) return;
    let cancelled = false;
    setLoading(true);
    setSrc(null);

    async function load() {
      try {
        const dataUrl = await fetchPhoto(`/api/photos/${filename}`);
        if (!cancelled) {
          setSrc(dataUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error("Photo load error:", err);
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [filename]);

  return (
    <div style={{ position: "relative", width: "100%", minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {loading && <div className="photo-placeholder lightbox-placeholder" />}
      {src && (
        <img
          src={src}
          alt={alt}
          className={className}
          style={{ display: "block", maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }}
        />
      )}
    </div>
  );
}

export default AuthPhoto;
